<!-- Выбирает категорию расхода с поиском и созданием из селекта. -->
<template>
  <BaseSelect
    v-bind="$attrs"
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :external-search="true"
    :invalid="invalid"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="handleCreateButtonClick"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />

  <AddCategoryModal
    v-if="showCreateModal"
    :company-id="companyId"
    @close="showCreateModal = false"
    @categories-created="handleCreated"
  />
</template>

<script>
import { mapState } from "pinia";

import { useExpenseCategoryStore } from "@/stores/expenseCategoryStore";
import { getCompanyId } from "@/utils/userUtils";
import { expenseCategorySelectItems } from "@/utils/expenseCategorySelectUtils";
import AddCategoryModal from "@/components/AddCategoryModal.vue";

export default {
  name: "ExpenseCategorySelect",

  inheritAttrs: false,

  components: {
    AddCategoryModal,
  },

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите категорию расходов",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    withButton: {
      type: Boolean,
      default: true,
    },
    buttonLabel: {
      type: String,
      default: "Добавить новую категорию",
    },
    noResultsText: {
      type: String,
      default: "Категории не найдены",
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    internalCreate: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["update:modelValue", "change", "button-click"],

  data() {
    return {
      companyId: getCompanyId(),
      preselectedOption: null,
      isSearchActive: false,
      showCreateModal: false,
    };
  },

  computed: {
    ...mapState(useExpenseCategoryStore, [
      "expenseCategories",
      "pagination",
      "dataIsLoading",
      "selectItems",
      "selectPagination",
      "selectIsLoading",
    ]),

    formattedOptions() {
      const categories = expenseCategorySelectItems({
        selectItems: this.selectItems,
        expenseCategories: this.expenseCategories,
        isSearchActive: this.isSearchActive,
      });

      const options = categories.map((category) => {
        const label = category.emoji
          ? `${category.emoji} ${category.name}`
          : category.name || `Категория #${category.id}`;

        return {
          value: category.id,
          label,
        };
      });

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }

      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
        };
      } else {
        this.preselectedOption = null;
      }

      this.$emit("change", option ? option.value : null);
    },

    handleCreateButtonClick() {
      if (this.internalCreate) {
        this.showCreateModal = true;
        return;
      }

      this.$emit("button-click");
    },

    async handleCreated(category) {
      this.showCreateModal = false;

      const store = useExpenseCategoryStore();
      await store.fetchSelectItems();

      if (!category?.id) return;

      this.preselectedOption = {
        value: category.id,
        label: this.categoryLabel(category),
      };
      this.$emit("update:modelValue", category.id);
      this.$emit("change", category.id);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      const store = useExpenseCategoryStore();
      await store.fetchSelectItems({
        search: searchData.query,
      });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      const store = useExpenseCategoryStore();
      await store.fetchSelectItems();
    },

    async handleScrollEnd() {
      const store = useExpenseCategoryStore();
      if (
        store.selectPagination.page < store.selectPagination.pages &&
        !store.selectIsLoading
      ) {
        await store.loadMoreSelectItems();
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const store = useExpenseCategoryStore();
      const exists = store.selectItems.find(
        (c) => String(c.id) === String(this.modelValue)
      );
      if (exists) return;

      const category = await store.fetchSelectItem(this.modelValue);

      this.preselectedOption = {
        value: category.id,
        label: this.categoryLabel(category),
      };
    },

    categoryLabel(category) {
      return category.emoji
        ? `${category.emoji} ${category.name}`
        : category.name || `Категория #${category.id}`;
    },
  },

  async mounted() {
    const store = useExpenseCategoryStore();

    await store.fetchSelectItems();
    await this.fetchPreselectedOption();
  },
};
</script>
